import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Linkedin, Facebook, Instagram, MapPin, MessageCircle } from 'lucide-react';
import { contactInfo, WHATSAPP_DEFAULT } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Certifications', path: '/certifications' },
    { name: 'Export Terms', path: '/export-terms' },
    { name: 'Contact', path: '/contact' }
  ];

  const productLinks = [
    { name: 'Cane Jaggery', path: '/products/1' },
    { name: 'Jaggery Powder', path: '/products/2' },
    { name: 'Onion Powder', path: '/products/3' },
    { name: 'Premium Basmati Rice', path: '/products/7' },
    { name: 'Dehydrated Banana Powder', path: '/products/9' },
    { name: 'Fresh Vegetables', path: '/products/8' }
  ];

  return (
    <footer className="bg-gradient-to-br from-green-900 via-green-800 to-green-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center group inline-block">
              <img
                src="https://customer-assets.emergentagent.com/job_b060ace3-0bc6-4db8-bd84-7f92111eacd1/artifacts/g9ds7k43_WhatsApp%20Image%202026-02-03%20at%205.40.27%20PM.jpeg"
                alt="Agro Corp India Logo"
                className="h-20 w-auto object-contain transform group-hover:scale-105 transition-transform duration-300 p-3 bg-white/10 backdrop-blur-sm rounded-lg"
              />
            </Link>
            <p className="text-green-100 text-sm leading-relaxed">
              Trusted exporter of natural agro products, committed to quality, purity, and reliability in every shipment worldwide.
            </p>
            <div className="flex space-x-3">
              <a href={contactInfo.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-700 hover:bg-amber-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110" data-testid="footer-linkedin">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href={contactInfo.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-700 hover:bg-amber-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110" data-testid="footer-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={contactInfo.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-700 hover:bg-amber-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110" data-testid="footer-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-700 hover:bg-green-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110" data-testid="footer-whatsapp">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-amber-400 font-montserrat">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-green-100 hover:text-amber-400 transition-colors duration-300 text-sm inline-block hover:translate-x-1 transform">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-amber-400 font-montserrat">Our Products</h3>
            <ul className="space-y-2">
              {productLinks.map((product) => (
                <li key={product.path}>
                  <Link to={product.path} className="text-green-100 hover:text-amber-400 transition-colors duration-300 text-sm inline-block hover:translate-x-1 transform">
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-amber-400 font-montserrat">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span className="text-green-100 text-sm" data-testid="footer-address">{contactInfo.address}</span>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${contactInfo.email}`} className="text-green-100 hover:text-amber-400 transition-colors duration-300 text-sm" data-testid="footer-email">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MessageCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer" className="text-green-100 hover:text-amber-400 transition-colors duration-300 text-sm" data-testid="footer-whatsapp-link">
                  WhatsApp: +91 98234 90072
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-green-100 text-sm">
                  <a href={`tel:${contactInfo.phone}`} className="hover:text-amber-400 transition-colors duration-300 block">{contactInfo.phone}</a>
                  <a href={`tel:${contactInfo.phone2}`} className="hover:text-amber-400 transition-colors duration-300 block">{contactInfo.phone2}</a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-green-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-green-200 text-sm" data-testid="footer-copyright">
              &copy; {currentYear} Agro Corp India. All rights reserved. | Regd. Office: Daund, Pune, Maharashtra, India
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/export-terms" className="text-green-200 hover:text-amber-400 transition-colors duration-300">Export Terms</Link>
              <Link to="/contact" className="text-green-200 hover:text-amber-400 transition-colors duration-300">Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
